import React, { useState } from "react";
import styles from "./MetadataModal.module.css";
import { capitalize, formatDateTime } from "../../utils";
import { Tooltip } from "../Tooltip";

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const MetadataModal = ({ isOpen, onClose, metadataObj, identityTypeMap }: { isOpen: any; onClose: any; metadataObj: any; identityTypeMap?: Map<string, string> }) => {
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  if (!isOpen) return null;

  const handleCopy = (label: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedLabel(label);
      setTimeout(() => setCopiedLabel(null), 2000);
    });
  };

  const contributorNames = (() => {
    const ids = metadataObj?.contributor_organization_ids || [];
    const components = metadataObj?.result_set_components || [];
    const names = ids
      .map((id) => {
        const match = components.find((c) => c.org_ids?.split(" ").includes(id));
        return match?.org_names || null;
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : null;
  })();

  const formatExpiry = (val) => {
    if (!val) return null;
    return val;
  };

  const datasetFilterRows = (metadataObj?.metadata?.dataset_filters || []).map((ds: any) => ({
    label: ds.dataset_name,
    value: (ds.filter_strings || []).join(", "),
  }));

  const rows = [
    {
      label: "Timestamp",
      value: metadataObj?.timestamp
        ? formatDateTime(metadataObj.timestamp)
        : null,
    },
    { label: "Cohort Name", value: metadataObj?.campaign_name },
    { label: "ID Type", value: identityTypeMap?.get(metadataObj?.metadata?.identifier) ?? metadataObj?.metadata?.identifier },
    { label: "Query Objective", value: metadataObj?.metadata?.query_objective ? capitalize(metadataObj.metadata.query_objective) : null },
    { label: "Query ID", value: metadataObj?.query_id },
    { label: "Expiry On", value: formatExpiry(metadataObj?.metadata?.expiry_duration) },
    { label: "Content", value: identityTypeMap?.get(metadataObj?.metadata?.content) ?? metadataObj?.metadata?.content },
    { label: "Transaction Hash", value: metadataObj?.tx_hash, isHash: true },
    { label: "Block Hash", value: metadataObj?.block_hash, isHash: true },
    { label: "Contributor Org(s)", value: contributorNames },
  ];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            <svg className={styles.modalTitleIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Metadata
          </h3>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={styles.modalBody}>
          {rows.map(({ label, value, isHash }) => {
            if (!value) return null;
            return (
              <div key={label} className={styles.infoRow}>
                <span className={styles.infoLabel}>{label}</span>
                <span className={styles.hashRowValue}>
                  <span className={isHash ? styles.hashValue : styles.infoValue}>{value}</span>
                  {isHash && (
                    <Tooltip text="Copy to clipboard" placement="top">
                      <button
                        className={styles.copyBtn}
                        onClick={() => handleCopy(label, value)}
                      >
                        {copiedLabel === label ? (
                          <span className={styles.copiedText}>Copied!</span>
                        ) : (
                          <CopyIcon />
                        )}
                      </button>
                    </Tooltip>
                  )}
                </span>
              </div>
            );
          })}

          {datasetFilterRows.length > 0 && (
            <>
              <div className={styles.sectionHeader}>Query Filters</div>
              <table className={styles.filterTable}>
                <thead>
                  <tr>
                    <th className={styles.filterTableHeader}>Dataset</th>
                    <th className={styles.filterTableHeader}>Filter</th>
                  </tr>
                </thead>
                <tbody>
                  {datasetFilterRows.map(({ label, value }) => (
                    <tr key={label}>
                      <td className={styles.filterTableCell}>{label}</td>
                      <td className={styles.filterTableCell}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetadataModal;
